"use client";

import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthenticationContext";
import {
    NavigationMenu,
    NavigationMenuContent,
    NavigationMenuItem,
    NavigationMenuList,
    NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Loader2, Moon, Sun, UserCircle } from "lucide-react";
import { FeedbackPanel } from "@/components/feedback/FeedbackPanel";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import Link from "next/link";
import { useRouter, usePathname } from "next/navigation";
import versionData from "../../app/version.json";
import { cn } from "@/lib/utils";
import Image from "next/image";

export function StandardNavigation() {
    const router = useRouter();
    const pathname = usePathname();
    const { theme, toggleTheme } = useTheme();
    const { user, isLoading, signOut } = useAuth();

    const handleSignOut = async () => {
        try {
            await signOut();
            router.replace("/auth/signin");
        } catch (error) {
            console.error("Error signing out:", error);
        }
    };

    return (
        <header className="sticky top-0 w-full py-3 px-4 bg-background-50 flex items-center justify-between border-b border-border z-50 print:hidden">
            <div className="flex items-center gap-1">
                <Link
                    href={"/"}
                    className="font-sans text-sm leading-none tracking-[0.09em] uppercase px-3 py-[4.5px] mr-1.5 flex items-center"
                    title={"Story Stream - Version " + versionData.version}
                >
                    <Image
                        src="/favicon.ico"
                        alt="Story Stream"
                        width={24}
                        height={24}
                        priority={true}
                        className="mr-1"
                    />
                    Story Stream
                </Link>
                <Link
                    href={`/books`}
                    className={cn(
                        "text-small text-theme-black inline-flex gap-1 items-center py-2 px-2.5 hover:underline mx-1.5",
                        pathname === "/books" ? "font-semibold" : "font-normal"
                    )}
                >
                    My Books
                </Link>
                <Link
                    href={`/docs`}
                    className={cn(
                        "text-small text-theme-black py-2 px-2.5 hover:underline transition-all mx-1.5",
                        pathname === "/docs" ? "font-semibold" : "font-normal"
                    )}
                >
                    Documentation
                </Link>
            </div>
            <div className="flex items-center gap-2.5">
                {!isLoading ? (
                    <>
                        {user ? (
                            <NavigationMenu>
                                <NavigationMenuList>
                                    <NavigationMenuItem>
                                        <NavigationMenuTrigger
                                            className="flex items-center gap-x-2 bg-background-100"
                                            showChevron={false}
                                        >
                                            <UserCircle className="size-4" />
                                            <span className="text-small font-medium hidden md:inline-block">
                                                {user.email}
                                            </span>
                                        </NavigationMenuTrigger>
                                        <NavigationMenuContent>
                                            <ul className="w-48 p-2">
                                                <li>
                                                    <button
                                                        onClick={handleSignOut}
                                                        className="w-full text-left px-4 py-2 text-sm hover:bg-accent rounded-md"
                                                    >
                                                        Sign Out
                                                    </button>
                                                </li>
                                            </ul>
                                        </NavigationMenuContent>
                                    </NavigationMenuItem>
                                </NavigationMenuList>
                            </NavigationMenu>
                        ) : (
                            <Link
                                href={"/auth/signin"}
                                className="text-small text-theme-black inline-flex gap-1 items-center py-2 px-4 hover:underline"
                            >
                                Sign In
                            </Link>
                        )}
                    </>
                ) : (
                    <div className="h-10 px-4 py-1 flex items-center gap-x-2 w-[200px] rounded-md bg-background-100">
                        <Loader2 className="size-5 animate-spin text-[var(--accent-primary)] inline-block" />
                        <div className="bg-theme-black opacity-10 w-full h-[21px]"></div>
                    </div>
                )}
                {!isLoading ? (
                    <TooltipProvider delayDuration={0}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    onClick={toggleTheme}
                                    className="inline-flex items-center justify-center rounded-md h-10 w-10 hover:bg-accent hover:text-accent-foreground"
                                    aria-label="Toggle theme"
                                >
                                    {theme === "light" ? (
                                        <Moon className="h-5 w-5" />
                                    ) : (
                                        <Sun className="h-5 w-5" />
                                    )}
                                </button>
                            </TooltipTrigger>
                            <TooltipContent>Change Theme</TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                ) : (
                    <div className="size-10 flex items-center justify-center rounded-md bg-background-100">
                        <Loader2 className="size-5 animate-spin text-[var(--accent-primary)] inline-block" />
                    </div>
                )}
                <FeedbackPanel
                    locationElement="TopNavFeedbackButton"
                    iconOnly
                    tooltipText="Feedback"
                />
            </div>
        </header>
    );
}
